import React, { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { attachPaymentMethod, getCustomerPortal } from 'services/clients/subscription';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { OnFormIsValidOptions } from 'components/PaymentForm/interfaces';
import { useSnackbar } from 'common/hooks/snackbar';
import { PaymentForm, proccessPaymentFormData } from 'components/PaymentForm';
import { Button } from '@mui/material';
import axios from 'axios';
import { useSegment } from 'common/hooks/segment';

const updateDefaultPaymentMethod = async (
  formData: OnFormIsValidOptions | undefined
): Promise<{ errorMsg: string | null }> => {
  if (!formData) {
    return { errorMsg: null };
  }

  const { error: paymentMethodError, paymentInformation } = await proccessPaymentFormData({
    formData,
  });

  if (paymentMethodError || !paymentInformation) {
    return {
      errorMsg:
        'Error while processing your credit card. Please try again or enter a different credit card.',
    };
  }
  // This is annoying to fix because it is going to JS
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { error } = await attachPaymentMethod({
    ...paymentInformation,
  });
  if (error) {
    if (axios.isAxiosError(error)) {
      return {
        errorMsg:
          error.response?.data.message ||
          'Error while processing your credit card. Please try again or enter a different payment method.',
      };
    }
    return {
      errorMsg:
        'Error while processing your payment method. Please try again or enter a different payment method.',
    };
  }
  return { errorMsg: null };
};

const SuccessMessage = () => (
  <h4>
    <span className="font-bold">Success! </span>
    New payment method saved as default.
  </h4>
);

const InvoiceHistoryPage = () => {
  const [customerPortalUrl, setCustomerPortalUrl] = useState();
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [renderPaymentForm, setRenderPaymentForm] = useState(Date.now());
  const [formValues, setFormValues] = useState<OnFormIsValidOptions>();
  const displaySnackbar = useSnackbar();

  const { trackEventNoSlack } = useSegment();

  useEffect(() => {
    (async () => {
      const data = await getCustomerPortal();
      setCustomerPortalUrl(data.customerPortalUrl);
    })();
  }, []);

  const handleOnClick = async () => {
    setIsLoading(true);
    const { errorMsg } = await updateDefaultPaymentMethod(formValues);
    if (errorMsg) {
      displaySnackbar({
        severity: 'error',
        text: errorMsg,
      });
    } else {
      setRenderPaymentForm(Date.now());
      displaySnackbar({
        html: () => <SuccessMessage />,
      });
    }
    setIsLoading(false);
  };

  const onFormIsValid = React.useCallback(
    (formIsValid: boolean, values?: OnFormIsValidOptions) => {
      setIsValid(formIsValid);
      setFormValues(values);
    },
    [setIsValid, setFormValues]
  );

  return (
    <div className="flex flex-col space-y-8 items-start px-8 p-12">
      <div className="flex flex-col items-start space-y-4">
        <div className="flex flex-col">
          <h2 className="font-medium text-dark">Invoices</h2>
          <div className="w-full bg-dark h-px" />
        </div>
        <p className="text-dark">Access all of your invoices on your customer portal.</p>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          endIcon={<ArrowRightAltIcon />}
          disabled={!customerPortalUrl}
          onClick={() => {
            window.open(customerPortalUrl);
            trackEventNoSlack('Customer Portal Clicked', {});
          }}
        >
          <h4 className="font-bold">Access your customer portal</h4>
        </Button>
      </div>

      <div className="flex flex-col items-start space-y-4">
        <div className="flex flex-col">
          <h2 className="font-medium text-dark">Billing Information</h2>
          <div className="w-full bg-dark h-px" />
        </div>
        <div className="min-w-160 flex flex-col space-y-4">
          <PaymentForm key={renderPaymentForm} maxWidth={650} onFormIsValid={onFormIsValid} />
          <LoadingButton
            loading={isLoading}
            variant="contained"
            sx={{
              marginLeft: 'auto',
            }}
            disabled={!isValid}
            onClick={handleOnClick}
          >
            <h4 className="font-medium text-white">Save as default payment method</h4>
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

export default InvoiceHistoryPage;
